import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import {
  IElement,
  IElementDynamicTiledMap,
  IElementSection,
  isIElementDynamicTiledMap,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectChildrenDepthFirst,
  selectDatasetOrArea,
  selectIElement,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { uniqBy } from "es-toolkit";

/**
 * @returns the dynamic overview maps of the area, of the data sets aligned to it,
 * and of the data set of the reference element
 * @param state current store state
 * @param area The active area
 * @param referenceElement The reference element of the scene, often a single scan
 */
export const selectAreaDynamicMaps = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, area?: IElementSection) => area,
      (
        state: OnlyIElementsStore,
        area?: IElementSection,
        referenceElement?: IElement,
      ) => referenceElement,
    ],
    computeAreaDynamicMaps,
  ),
);

function computeAreaDynamicMaps(
  iElements: IElementsState,
  area?: IElementSection,
  referenceElement?: IElement,
): IElementDynamicTiledMap[] {
  const state = { iElements };
  const dataSetIds = area
    ? (iElements.areaDataSets[area.id]?.map((ds) => ds.elementId) ?? [])
    : [];
  const parents = [
    area,
    // The reference element is often a single scan, while the map is a child of its data set
    referenceElement ? selectDatasetOrArea(referenceElement)(state) : undefined,
    ...dataSetIds.map((id) => selectIElement(id)(state)),
  ];
  return uniqBy(
    parents.flatMap((parent) =>
      selectChildrenDepthFirst(parent, isIElementDynamicTiledMap)(state),
    ),
    (map) => map.id,
  );
}
